import { computed, inject } from '@angular/core';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Store } from '@ngrx/store';
import { UserFeedbackService } from 'data-user-feedback';
import {
  IUserFeedbackAnswer,
  IUserFeedbackQuestion,
  IUserFeedbackQuestionContextType,
} from 'models-user-feedback';
import { pipe, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';

type UserFeedbackStore = {
  question: null | IUserFeedbackQuestion;
  loaded: boolean;
  submitting: boolean;
  submitted: boolean;
};

const initialState: UserFeedbackStore = {
  question: null,
  loaded: false,
  submitting: false,
  submitted: false,
};

// tslint:disable-next-line:variable-name
export const UserFeedbackStore = signalStore(
  withState(initialState),
  withComputed(({ loaded, question }) => ({
    noQuestionAvailable: computed(() => loaded() && !question()),
  })),
  withMethods(
    (
      state,
      userFeedbackService = inject(UserFeedbackService),
      store = inject(Store),
    ) => {
      return {
        loadQuestion: rxMethod<IUserFeedbackQuestionContextType>(
          pipe(
            switchMap((contextType) =>
              userFeedbackService.getQuestion(contextType).pipe(
                tapResponse({
                  next: (question) => {
                    patchState(state, {
                      loaded: true,
                      question,
                      submitted: false,
                    });
                  },
                  error: () => patchState(state, { loaded: true }),
                }),
              ),
            ),
          ),
        ),
        submitAnswer: rxMethod<IUserFeedbackAnswer>(
          pipe(
            tap(() => patchState(state, { submitting: true })),
            switchMap((answer) =>
              userFeedbackService.submitAnswer(answer).pipe(
                tapResponse({
                  next: () => {
                    store.dispatch(
                      showSnackBarMessage({
                        message: $localize`:cBasket|Submit answer success message@@submitAnswerSuccess:Thanks for your feedback! We truly appreciate your insight and your time`,
                        messageType: MessageType.SUCCESS,
                      }),
                    );
                    patchState(state, { submitting: false, submitted: true });
                  },
                  error: () => {
                    store.dispatch(
                      showSnackBarMessage({
                        message: $localize`:cBasket|Submit answer error message@@submitAnswerError:An error occurred while submitting your feedback. Please try again later.`,
                        messageType: MessageType.WARNING,
                      }),
                    );
                    patchState(state, { submitting: false });
                  },
                }),
              ),
            ),
          ),
        ),
      };
    },
  ),
);
